/**
 * hero-effect.js — Gold Canna animated hero background engine.
 *
 * Renders a canvas-based "gold drift" light field (blobs of color moving
 * according to a selectable movement mode) optionally refracted through a
 * selectable glass texture, with a specular highlight pass on top. This is
 * the production extraction of the `hero-gold-drift-v2.html` review artifact:
 * the Tune panel, demo fonts, and demo heading/subheading markup have been
 * stripped. Only the render engine, the tilt/parallax input, the
 * config-reading bootstrap, and the offscreen-pause behavior remain.
 *
 * ## Required markup
 * The section that hosts this effect MUST render:
 *   1. `<section class="canna-hero-section">` — the hero root. Used both as
 *      the IntersectionObserver target (for offscreen pause) and as the
 *      pointer target for drag-to-simulate tilt.
 *   2. `<canvas id="hero-effect-canvas">` — the render target. If this
 *      element is not present the script exits immediately and does nothing
 *      (the section falls back to its static, non-canvas appearance).
 *
 * ## Optional config injection
 * Liquid may emit a sibling JSON config block:
 *
 *   <script type="application/json" id="hero-effect-config">
 *     { "movement": "Lava Lamp", "texture": "Fluted", "opacity": 0.12, ... }
 *   </script>
 *
 * See `.specify/systems/system-07-theme-foundation/features/
 * 035-hero-background-effects/contracts/hero-effect-config.schema.json` for
 * the authoritative shape. If the block is absent, empty, or fails to parse,
 * the engine silently falls back to its built-in defaults — this file is
 * always safe to load with zero configuration.
 *
 * ## Behavior notes
 * - `prefers-reduced-motion: reduce` renders exactly one static frame and
 *   never starts the animation loop (no rAF is ever scheduled).
 * - When the hero section scrolls offscreen, an IntersectionObserver pauses
 *   the `requestAnimationFrame` loop; it resumes (re-anchoring the clock so
 *   time doesn't jump) when the section re-enters the viewport.
 * - Device-orientation tilt (with pointer-drag simulation as a fallback) adds
 *   a subtle parallax highlight/position shift; both are purely additive and
 *   never required for the effect to render correctly.
 */
(function () {
  /**
   * CLEAN admin label -> internal movement code key. Mirrors the
   * `movement` choices stored on the `hero_background_effect` metaobject
   * (data-model.md). Unknown or blank labels fall back to `lava`. Values
   * that are already a valid code key pass through unchanged, so a preset
   * that stores the code key directly still works.
   * @type {Object<string, string>}
   */
  const MOVEMENT_MAP = {
    "Lava Lamp": "lava",
    Smoke: "smoke",
    Waterfall: "fall",
    Orbit: "orbit",
    Current: "current",
    Radiate: "radiate",
    Disco: "disco",
    Rain: "rain",
  };
  const MOVEMENT_KEYS = new Set(Object.values(MOVEMENT_MAP));

  /**
   * CLEAN admin label -> internal texture code key. Mirrors the `texture`
   * choices stored on the `hero_background_effect` metaobject
   * (data-model.md). Unknown or blank labels fall back to `fluted`. Values
   * that are already a valid code key pass through unchanged.
   * @type {Object<string, string>}
   */
  const TEXTURE_MAP = {
    None: "none",
    Fluted: "fluted",
    Reeded: "reeded",
    Diagonal: "diagonal",
    Wavy: "ripple",
    Melt: "melt",
    Moiré: "moire",
    Prism: "prism",
    "Smoke/cloud": "cloud",
    "Square tiles": "cubes",
    Diamonds: "diamonds",
  };
  const TEXTURE_KEYS = new Set(Object.values(TEXTURE_MAP));

  /**
   * Normalizes a CLEAN admin label (or an already-normalized code key) to an
   * internal code key using the supplied map, falling back to `fallback`
   * when the value is blank or unrecognized.
   *
   * @param {string|undefined|null} value - The raw label/key from config.
   * @param {Object<string, string>} map - Label -> code key lookup.
   * @param {Set<string>} keySet - The set of valid code keys (for passthrough).
   * @param {string} fallback - Code key to use when value is blank/unknown.
   * @returns {string} A valid internal code key.
   */
  function normalizeKey(value, map, keySet, fallback) {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (Object.prototype.hasOwnProperty.call(map, trimmed)) return map[trimmed];
    if (keySet.has(trimmed)) return trimmed;
    return fallback;
  }

  /**
   * Tests whether a string is a valid `#rrggbb` hex color.
   * @param {string} value - Candidate hex string.
   * @returns {boolean} True if `value` matches `^#[0-9a-fA-F]{6}$`.
   */
  function isValidHex(value) {
    return typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);
  }

  const HERO_SECTION_SELECTOR = ".canna-hero-section";
  const CANVAS_ID = "hero-effect-canvas";
  const CONFIG_SCRIPT_ID = "hero-effect-config";

  const canvas = document.getElementById(CANVAS_ID);
  // No canvas on the page (no effect assigned to this collection) — the
  // section renders its static fallback with no JS work performed.
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  const heroSection = document.querySelector(HERO_SECTION_SELECTOR);
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  // ---- Tunables (built-in defaults; overridden by injected config below) ----
  const settings = {
    movement: "lava",
    opacity: 0.12,
    speed: 10,
    count: 5,
    sizeScale: 1,
    edgeVariance: 0.2,
    texture: "fluted",
    glassStrength: 14,
    glassScale: 20,
    highlight: 0.05,
    breath: 1, // multiplier on each light's size oscillation (0 = frozen sizes)
    glowPulse: 0, // depth of each light's brightness wave (0 = steady, 1 = fades to dark at trough)
    parallax: 0.5, // depth effect: nearer lights move faster, run bigger, and shift more on tilt
  };

  // Base palette relationships (variants of #d4af37) — the picked color re-anchors these
  const BASE_GOLDS = [
    [212, 175, 55],
    [230, 190, 80],
    [200, 150, 40],
    [240, 210, 120],
  ];
  let GOLDS = BASE_GOLDS.map((c) => c.slice());

  // ---- Color helpers (RGB 0-255 <-> HSL 0-1) ----

  /**
   * Converts an RGB color (0-255 per channel) to HSL (each channel 0-1).
   * @param {number} r - Red channel, 0-255.
   * @param {number} g - Green channel, 0-255.
   * @param {number} b - Blue channel, 0-255.
   * @returns {number[]} `[h, s, l]`, each in the range 0-1.
   */
  function rgb2hsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const mx = Math.max(r, g, b),
      mn = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (mx + mn) / 2;
    if (mx !== mn) {
      const d = mx - mn;
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (mx === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return [h, s, l];
  }

  /**
   * Converts an HSL color (each channel 0-1) to RGB (0-255 per channel).
   * @param {number} h - Hue, 0-1.
   * @param {number} s - Saturation, 0-1.
   * @param {number} l - Lightness, 0-1.
   * @returns {number[]} `[r, g, b]`, each in the range 0-255 (rounded).
   */
  function hsl2rgb(h, s, l) {
    h = ((h % 1) + 1) % 1;
    if (s === 0) {
      const v = Math.round(l * 255);
      return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    /**
     * Hue-to-channel interpolation for one RGB component (standard HSL→RGB).
     * @param {number} t Channel-shifted hue in [0,1) (h±1/3 for R/B, h for G).
     * @returns {number} The channel value in [0,1].
     */
    function f(t) {
      t = ((t % 1) + 1) % 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    return [
      Math.round(f(h + 1 / 3) * 255),
      Math.round(f(h) * 255),
      Math.round(f(h - 1 / 3) * 255),
    ];
  }

  /**
   * Clamps a number to the [0, 1] range.
   * @param {number} v - Input value.
   * @returns {number} `v` clamped to [0, 1].
   */
  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  // Rebuild the 4-color palette around the picked color, preserving the
  // hue/saturation/lightness spread of the original gold family
  const BASE_HSL = rgb2hsl(212, 175, 55);

  /**
   * Rebuilds the 4-color `GOLDS` palette around a new base hex color,
   * preserving the hue/saturation/lightness offsets of the original gold
   * family (`BASE_GOLDS`) relative to `BASE_HSL`.
   * @param {string} hex - A `#rrggbb` hex color string.
   * @returns {void}
   */
  function rebuildPalette(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const picked = rgb2hsl(r, g, b);
    GOLDS = BASE_GOLDS.map((base) => {
      const bh = rgb2hsl(base[0], base[1], base[2]);
      return hsl2rgb(
        picked[0] + (bh[0] - BASE_HSL[0]),
        clamp01(picked[1] + (bh[1] - BASE_HSL[1])),
        clamp01(picked[2] + (bh[2] - BASE_HSL[2])),
      );
    });
  }

  const MARGIN = 40;
  const BAND = 2;
  const BLUR_DOWNSCALE = 10;
  const THETA = Math.PI / 4; // 45° for the diagonal texture family

  let W = 0,
    H = 0,
    EW = 0,
    EH = 0,
    R = 0;
  let blobs = [];

  // Offscreen stages
  const small = document.createElement("canvas");
  const mid = document.createElement("canvas");
  const temp = document.createElement("canvas");
  const rotA = document.createElement("canvas"); // rotated workspace A
  const rotB = document.createElement("canvas"); // rotated workspace B
  const hl = document.createElement("canvas");
  const tile = document.createElement("canvas");
  const lo = document.createElement("canvas"); // low-res buffer for per-pixel cloud displacement
  const loctx = lo.getContext("2d", { willReadFrequently: true });
  const sctx = small.getContext("2d");
  const mctx = mid.getContext("2d");
  const tctx = temp.getContext("2d");
  const ractx = rotA.getContext("2d");
  const rbctx = rotB.getContext("2d");
  const hctx = hl.getContext("2d");

  let hlPattern = null;

  const glass = {
    p1: Math.random() * Math.PI * 2,
    p2: Math.random() * Math.PI * 2,
    p3: Math.random() * Math.PI * 2,
    p4: Math.random() * Math.PI * 2,
  };

  const tilt = { on: false, x: 0, y: 0, baseBeta: null, baseGamma: null };

  // ---- Cloud noise field (for the smoke/cloud texture) ----
  // Tileable organic noise built from plane waves with integer frequencies.
  // Two independent fields drive x- and y-displacement per pixel.
  const CLOUD_Q = 0.33; // displacement is computed at this fraction of full res
  let cloudDX = null,
    cloudDY = null,
    cloudOut = null,
    loW = 0,
    loH = 0;
  const cloudWaves = [];
  /**
   * Build the two tileable plane-wave sets that drive the cloud texture's
   * per-pixel x/y displacement. Runs once at module init; populates cloudWaves.
   * @returns {void}
   */
  (function buildCloudWaveSets() {
    for (let s = 0; s < 2; s++) {
      const set = [];
      let norm = 0;
      for (let k = 0; k < 6; k++) {
        let i = 0,
          j = 0;
        while (i === 0 && j === 0) {
          i = Math.floor(rand(-3, 4));
          j = Math.floor(rand(-3, 4));
        }
        const a = 1 / (1 + k * 0.7);
        norm += a;
        set.push({ i, j, a, ph: rand(0, Math.PI * 2) });
      }
      set.norm = norm;
      cloudWaves.push(set);
    }
  })();

  /**
   * Samples one plane-wave noise set at a given point and "time" (tile
   * period), used to build the tileable cloud displacement field.
   * @param {Array<Object>} set - A wave set from `cloudWaves` (has `.norm`).
   * @param {number} x - Sample x-coordinate.
   * @param {number} y - Sample y-coordinate.
   * @param {number} T - Tile period.
   * @returns {number} Normalized wave sample, roughly in [-1, 1].
   */
  function sampleWave(set, x, y, T) {
    let v = 0;
    for (const w of set)
      v += w.a * Math.sin((2 * Math.PI * (w.i * x + w.j * y)) / T + w.ph);
    return v / set.norm;
  }

  /**
   * Rebuilds the low-resolution cloud displacement field (`cloudDX`/`cloudDY`)
   * used by the `cloud` texture's per-pixel refraction pass. Must be called
   * whenever the canvas size or `glassScale` changes.
   * @returns {void}
   */
  function buildCloudField() {
    loW = Math.max(4, Math.round(EW * CLOUD_Q));
    loH = Math.max(4, Math.round(EH * CLOUD_Q));
    lo.width = loW;
    lo.height = loH;
    const T = Math.max(60, settings.glassScale * 6);
    cloudDX = new Float32Array(loW * loH);
    cloudDY = new Float32Array(loW * loH);
    for (let y = 0; y < loH; y++) {
      const yc = y / CLOUD_Q;
      for (let x = 0; x < loW; x++) {
        const xc = x / CLOUD_Q;
        const idx = y * loW + x;
        cloudDX[idx] = sampleWave(cloudWaves[0], xc, yc, T);
        cloudDY[idx] = sampleWave(cloudWaves[1], xc, yc, T);
      }
    }
    cloudOut = loctx.createImageData(loW, loH);
  }

  /**
   * Recomputes canvas and offscreen-stage dimensions from the canvas's
   * current CSS box size, and rebuilds the cloud displacement field to
   * match. Called on init and on window resize.
   * @returns {void}
   */
  function resize() {
    W = canvas.clientWidth;
    H = canvas.clientHeight;
    EW = W + MARGIN * 2;
    EH = H + MARGIN * 2;
    // Rotated workspace must contain the extended image at 45°
    R = Math.min(2400, Math.ceil((EW + EH) / Math.SQRT2) + 8);
    canvas.width = W;
    canvas.height = H;
    mid.width = EW;
    mid.height = EH;
    temp.width = EW;
    temp.height = EH;
    rotA.width = R;
    rotA.height = R;
    rotB.width = R;
    rotB.height = R;
    hl.width = W;
    hl.height = H;
    small.width = Math.max(2, Math.round(EW / BLUR_DOWNSCALE));
    small.height = Math.max(2, Math.round(EH / BLUR_DOWNSCALE));
    mctx.imageSmoothingEnabled = true;
    mctx.imageSmoothingQuality = "high";
    buildCloudField();
  }

  /**
   * Returns a pseudo-random float in `[min, max)`.
   * @param {number} min - Lower bound (inclusive).
   * @param {number} max - Upper bound (exclusive).
   * @returns {number} Random float in `[min, max)`.
   */
  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  /**
   * Creates one blob (light) with randomized position, motion, color, and
   * per-movement-mode parameters. See `blobPos` for how these parameters are
   * consumed by each movement mode.
   * @returns {Object} A new blob descriptor.
   */
  function makeBlob() {
    const base = Math.min(W, H);
    const ci = Math.floor(Math.random() * GOLDS.length);
    return {
      ax: rand(0.05, 0.95) * W,
      ay: rand(0.05, 0.95) * H,
      ampX: rand(0.15, 0.4) * W,
      ampY: rand(0.15, 0.4) * H,
      f1: rand(0.02, 0.05),
      f2: rand(0.008, 0.02),
      f3: rand(0.02, 0.05),
      f4: rand(0.008, 0.02),
      p1: rand(0, Math.PI * 2),
      p2: rand(0, Math.PI * 2),
      p3: rand(0, Math.PI * 2),
      p4: rand(0, Math.PI * 2),
      rBase: rand(0.28, 0.55) * base,
      rAmp: rand(0.05, 0.15) * base,
      rFreq: rand(0.01, 0.03),
      rPhase: rand(0, Math.PI * 2),
      ci,
      alphaMul: rand(0.7, 1.0),
      // personal brightness-wave rhythm for glow pulse
      gFreq: rand(0.008, 0.025),
      gPhase: rand(0, Math.PI * 2),
      // movement-mode parameters
      vLin: rand(1 / 600, 1 / 240), // linear travel speed (fraction of screen per t-unit)
      off0: rand(0, 100000), // travel cycle offset so lights don't move in rows
      orbSpeed: rand(0.004, 0.012) * (Math.random() < 0.5 ? -1 : 1), // some clockwise, some counter
      orbPhase: rand(0, Math.PI * 2),
      radX: rand(0.12, 0.45), // orbit ellipse radii (fractions of W/H)
      radY: rand(0.12, 0.45),
      dir: rand(0, Math.PI * 2), // radiate direction
      vr: rand(1 / 700, 1 / 300), // radiate speed
      off1: Math.random(),
      vRain: rand(1 / 60, 1 / 25), // rain drop cycle speed
      seedA: rand(1, 1000), // deterministic per-drop position seeds
      seedB: rand(1, 1000),
      z: rand(0.5, 1.6), // depth: <1 = far layer, >1 = near layer
      edge: Math.random(),
      sharpness: rand(0.55, 1.0),
    };
  }

  /**
   * (Re)builds the `blobs` array to `settings.count` entries. Called on init
   * and whenever `light_count` changes via injected config.
   * @returns {void}
   */
  function buildBlobs() {
    blobs = Array.from({ length: settings.count }, makeBlob);
  }

  // ---- Movement modes ----

  /**
   * Computes one blob's position (and optional size/fade/stretch modifiers)
   * at time `t`, according to `settings.movement`. Wrapping is deterministic
   * in `t`, so pausing/resuming the render loop never causes a visible jump
   * as long as the caller re-anchors its own clock consistently.
   *
   * @param {Object} b - A blob descriptor from `makeBlob`.
   * @param {number} t - Current animation time (already scaled by speed).
   * @returns {{x: number, y: number, rMul: number, aMul?: number, minSharp?: number, stretch?: {angle: number, amt: number}}}
   *   Position, size multiplier, optional fade envelope, optional minimum
   *   sharpness, and optional directional stretch/smear.
   */
  function blobPos(b, t) {
    const m = settings.movement;
    const par = settings.parallax;
    // Depth-scaled time: nearer lights (z>1) move faster, far ones slower
    const zs = 1 + (b.z - 1) * par;
    const zSize = 1 + (b.z - 1) * par * 0.6; // nearer lights also run bigger
    // Tilt/drag parallax: layers shift by different amounts
    const pdx = tilt.x * (b.z - 1) * par * 1.5;
    const pdy = tilt.y * (b.z - 1) * par * 1.5;

    if (m === "smoke" || m === "fall") {
      const pad = 2 * b.rBase * settings.sizeScale;
      const span = H + 2 * pad;
      const cyc = (((t * zs * b.vLin * H + b.off0) % span) + span) % span;
      const prog = cyc / span;
      // Non-linear travel: smoke launches quickly then decelerates as it
      // disperses; waterfall accelerates as gravity takes hold
      const eased =
        m === "smoke" ? 1 - Math.pow(1 - prog, 1.8) : Math.pow(prog, 1.9);
      const y = m === "smoke" ? H + pad - eased * span : eased * span - pad;
      const hf = Math.min(1, Math.max(0, 1 - y / H));
      const sway = m === "smoke" ? 0.4 + 1.1 * hf : 0.4 + 1.1 * (1 - hf);
      const x =
        b.ax +
        Math.sin(t * zs * b.f1 + b.p1) * b.ampX * 0.22 * sway +
        Math.sin(t * zs * b.f2 + b.p2) * b.ampX * 0.12 * sway;
      return { x: x + pdx, y: y + pdy, rMul: zSize };
    }

    if (m === "orbit") {
      const a = t * zs * b.orbSpeed + b.orbPhase;
      return {
        x: W / 2 + Math.cos(a) * b.radX * W + pdx,
        y: H / 2 + Math.sin(a) * b.radY * H + pdy,
        rMul: zSize,
      };
    }

    if (m === "current") {
      const pad = 2 * b.rBase * settings.sizeScale;
      const span = W + 2 * pad;
      const cyc = (((t * zs * b.vLin * W * 1.3 + b.off0) % span) + span) % span;
      const x = cyc - pad;
      const y = b.ay + Math.sin(t * zs * b.f3 + b.p3) * b.ampY * 0.18;
      return { x: x + pdx, y: y + pdy, rMul: zSize };
    }

    if (m === "radiate") {
      const maxD = Math.hypot(W, H) / 2 + 2 * b.rBase * settings.sizeScale;
      const prog = (((t * zs * b.vr + b.off1) % 1) + 1) % 1;
      return {
        x: W / 2 + Math.cos(b.dir) * prog * maxD + pdx,
        y: H / 2 + Math.sin(b.dir) * prog * maxD + pdy,
        rMul: (0.35 + 0.75 * prog) * zSize,
        aMul: Math.min(1, prog / 0.12), // ease in from 0% so births don't pop
        // Lights smear outward as they near the edge, like light distorting
        // at the periphery of a lens
        stretch: { angle: b.dir, amt: Math.pow(prog, 1.6) },
      };
    }

    if (m === "disco") {
      const pad = 2 * b.rBase * settings.sizeScale;
      const span = W + 2 * pad;
      // Spots stay coordinated (reflections of one ball), with only a hint
      // of depth variance so parallax doesn't break the illusion
      const dzs = 1 + (b.z - 1) * par * 0.35;
      const sweep = t * dzs * (W / 45);
      const cyc = (((sweep + b.off0) % span) + span) % span;
      const roll =
        Math.sin(t * 0.05 + b.p3) * H * 0.04 + Math.sin(t * 0.11) * H * 0.03;
      return {
        x: cyc - pad + pdx * 0.5,
        y: b.ay + roll + pdy * 0.5,
        rMul: 0.3 * zSize,
        aMul: 1,
        minSharp: 0.75,
      };
    }

    if (m === "rain") {
      const cycles = t * zs * b.vRain + b.off1 * 100;
      const idx = Math.floor(cycles);
      const prog = cycles - idx;
      const hx = Math.sin(idx * 12.9898 + b.seedA) * 43758.5453;
      const hy = Math.sin(idx * 78.233 + b.seedB) * 43758.5453;
      const px = (hx - Math.floor(hx)) * W;
      const py = (hy - Math.floor(hy)) * H;
      // Ripple physics: the splash expands fast on impact, then decelerates
      // as it spreads — an ease-out curve on the growth
      const grow = 1 - Math.pow(1 - prog, 2.4);
      // Sharp bright flash at impact, then a long fade that tracks the
      // slowing expansion
      const aMul =
        prog < 0.08 ? prog / 0.08 : Math.pow(1 - (prog - 0.08) / 0.92, 1.6);
      return {
        x: px + pdx,
        y: py + pdy,
        rMul: (0.12 + grow * 1.15) * zSize,
        aMul,
      };
    }

    // 'lava' — the original layered-sine wander
    return {
      x:
        b.ax +
        Math.sin(t * zs * b.f1 + b.p1) * b.ampX * 0.6 +
        Math.sin(t * zs * b.f2 + b.p2) * b.ampX * 0.4 +
        pdx,
      y:
        b.ay +
        Math.sin(t * zs * b.f3 + b.p3) * b.ampY * 0.6 +
        Math.sin(t * zs * b.f4 + b.p4) * b.ampY * 0.4 +
        pdy,
      rMul: zSize,
    };
  }

  /**
   * Renders all blobs (lights) at time `t` into the low-resolution `small`
   * offscreen canvas, which is later upscaled/blurred into `mid` by
   * `composite`.
   * @param {number} t - Current animation time (already scaled by speed).
   * @returns {void}
   */
  function drawBlobs(t) {
    const s = small.width / EW;
    sctx.setTransform(s, 0, 0, s, 0, 0);
    sctx.clearRect(0, 0, EW, EH);
    sctx.translate(MARGIN, MARGIN);

    for (const b of blobs) {
      const pos = blobPos(b, t);
      const x = pos.x,
        y = pos.y;
      // Clamp: at high breathing values the wave can swing below zero, and a
      // negative radius throws in createRadialGradient. Floor it at "fully
      // exhaled" — the light simply vanishes for that part of its cycle.
      const r = Math.max(
        0.01,
        (b.rBase +
          Math.sin(t * b.rFreq + b.rPhase) * b.rAmp * settings.breath) *
          settings.sizeScale *
          pos.rMul,
      );
      if (r < 1) continue; // nothing meaningful to draw this frame

      // Glow pulse: each light rides its own slow brightness wave between
      // full intensity and (1 - glowPulse) of it — never synchronized
      const pulse =
        1 - settings.glowPulse * (0.5 + 0.5 * Math.sin(t * b.gFreq + b.gPhase));
      const fade = pos.aMul === undefined ? 1 : pos.aMul; // movement fade envelope
      const alpha = Math.max(0, settings.opacity * b.alphaMul * pulse * fade);
      const [cr, cg, cb] = GOLDS[b.ci];

      const ramp = Math.min(
        Math.max((settings.edgeVariance - b.edge) / 0.08, 0),
        1,
      );
      const sh = Math.max(b.sharpness * ramp, pos.minSharp || 0);
      const midStop = 0.6 + sh * 0.28;
      const midAlpha = alpha * (0.4 + sh * 0.55);

      const g = sctx.createRadialGradient(0, 0, 0, 0, 0, r);
      g.addColorStop(0, "rgba(" + cr + "," + cg + "," + cb + "," + alpha + ")");
      g.addColorStop(
        midStop,
        "rgba(" + cr + "," + cg + "," + cb + "," + midAlpha + ")",
      );
      g.addColorStop(1, "rgba(" + cr + "," + cg + "," + cb + ",0)");

      sctx.save();
      sctx.translate(x, y);
      if (pos.stretch && pos.stretch.amt > 0.01) {
        // Smear the light along its travel direction — grows with pos.stretch.amt
        sctx.rotate(pos.stretch.angle);
        sctx.scale(
          1 + pos.stretch.amt * 1.5,
          Math.max(0.45, 1 - pos.stretch.amt * 0.3),
        );
      }
      sctx.fillStyle = g;
      sctx.beginPath();
      sctx.arc(0, 0, r, 0, Math.PI * 2);
      sctx.fill();
      sctx.restore();
    }
  }

  // ---- Displacement wave functions ----

  /**
   * Simple sine displacement used by the `fluted`/`reeded`/`cubes` textures.
   * @param {number} v - Position along the displacement axis.
   * @returns {number} Displacement offset in pixels.
   */
  function waveSin(v) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return A * Math.sin((v * 2 * Math.PI) / L);
  }

  /**
   * Horizontal displacement as a function of `y` for the `ripple` texture's
   * first pass (two summed sine waves for an organic look).
   * @param {number} y - Row position.
   * @returns {number} Displacement offset in pixels.
   */
  function rippleDX(y) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return (
      A *
      (0.62 * Math.sin((y * 2 * Math.PI) / L + glass.p1) +
        0.38 * Math.sin((y * 2 * Math.PI) / (L * 0.531) + glass.p2))
    );
  }

  /**
   * Vertical displacement as a function of `x` for the `ripple` texture's
   * second pass.
   * @param {number} x - Column position.
   * @returns {number} Displacement offset in pixels.
   */
  function rippleDY(x) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return (
      A *
      (0.62 * Math.sin((x * 2 * Math.PI) / L + glass.p3) +
        0.38 * Math.sin((x * 2 * Math.PI) / (L * 0.517) + glass.p4))
    );
  }

  /**
   * Triangle wave in [-1, 1] with period `L` — produces a sharp, faceted
   * direction flip used by the `prism` texture.
   * @param {number} v - Input position.
   * @param {number} L - Wave period.
   * @returns {number} Triangle-wave value in [-1, 1].
   */
  function tri01(v, L) {
    const f = (((v / L) % 1) + 1) % 1;
    return f < 0.5 ? f * 4 - 1 : 3 - f * 4;
  }

  /**
   * Horizontal displacement for the `prism` texture (triangle-wave reeds).
   * @param {number} x - Column position.
   * @returns {number} Displacement offset in pixels.
   */
  function prismDX(x) {
    return settings.glassStrength * tri01(x, settings.glassScale);
  }

  /**
   * Vertical displacement ("drip") as a function of `x` for the `melt`
   * texture's first pass — chaotic layered waves.
   * @param {number} x - Column position.
   * @returns {number} Displacement offset in pixels.
   */
  function meltDY(x) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return (
      A *
      (0.5 * Math.sin((x * 2 * Math.PI) / L + glass.p3) +
        0.3 * Math.sin((x * 2 * Math.PI) / (L * 0.417) + glass.p4) +
        0.2 * Math.sin((x * 2 * Math.PI) / (L * 2.7) + glass.p1))
    );
  }

  /**
   * Horizontal "smear" displacement as a function of `y` for the `melt`
   * texture's second pass.
   * @param {number} y - Row position.
   * @returns {number} Displacement offset in pixels.
   */
  function meltDX(y) {
    const A = settings.glassStrength * 0.6,
      L = settings.glassScale;
    return (
      A *
      (0.6 * Math.sin((y * 2 * Math.PI) / (L * 1.3) + glass.p1) +
        0.4 * Math.sin((y * 2 * Math.PI) / (L * 0.61) + glass.p2))
    );
  }

  /**
   * Horizontal displacement as a function of `y` for the `moire` texture's
   * first pass — a coarse wave stacked with a fine wave, producing
   * interference bands.
   * @param {number} y - Row position.
   * @returns {number} Displacement offset in pixels.
   */
  function moireDX(y) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return (
      A *
      (0.55 * Math.sin((y * 2 * Math.PI) / L + glass.p1) +
        0.45 * Math.sin((y * 2 * Math.PI) / (L * 0.27) + glass.p2))
    );
  }

  /**
   * Vertical displacement as a function of `x` for the `moire` texture's
   * second pass.
   * @param {number} x - Column position.
   * @returns {number} Displacement offset in pixels.
   */
  function moireDY(x) {
    const A = settings.glassStrength,
      L = settings.glassScale;
    return (
      A *
      (0.55 * Math.sin((x * 2 * Math.PI) / L + glass.p3) +
        0.45 * Math.sin((x * 2 * Math.PI) / (L * 0.31) + glass.p4))
    );
  }

  /**
   * Clamps a displacement offset so the strip-pass sampling never reads
   * outside the extended (`MARGIN`-padded) source canvas.
   * @param {number} v - Raw displacement offset.
   * @returns {number} Offset clamped to `[-MARGIN + 1, MARGIN - 1]`.
   */
  function clampOff(v) {
    return Math.max(-MARGIN + 1, Math.min(MARGIN - 1, v));
  }

  // ---- Strip pass primitives ----

  /**
   * Draws `srcC` into `dctx` in vertical strips, each shifted horizontally
   * by `fn(x)`. Used by the `fluted`/`prism`/diagonal-family textures.
   * @param {HTMLCanvasElement} srcC - Source canvas.
   * @param {CanvasRenderingContext2D} dctx - Destination context.
   * @param {number} width - Destination width.
   * @param {number} height - Destination height.
   * @param {number} sx0 - Source x offset.
   * @param {number} sy0 - Source y offset.
   * @param {function(number): number} fn - Displacement function of x.
   * @returns {void}
   */
  function colShiftX(srcC, dctx, width, height, sx0, sy0, fn) {
    for (let x = 0; x < width; x += BAND) {
      const dx = clampOff(fn(x));
      dctx.drawImage(srcC, sx0 + x + dx, sy0, BAND, height, x, 0, BAND, height);
    }
  }

  /**
   * Draws `srcC` into `dctx` in horizontal strips, each shifted vertically
   * by `fn(y)`. Used by the `reeded`/cubes/diamonds texture families.
   * @param {HTMLCanvasElement} srcC - Source canvas.
   * @param {CanvasRenderingContext2D} dctx - Destination context.
   * @param {number} width - Destination width.
   * @param {number} height - Destination height.
   * @param {number} sx0 - Source x offset.
   * @param {number} sy0 - Source y offset.
   * @param {function(number): number} fn - Displacement function of y.
   * @returns {void}
   */
  function rowShiftY(srcC, dctx, width, height, sx0, sy0, fn) {
    for (let y = 0; y < height; y += BAND) {
      const dy = clampOff(fn(y));
      dctx.drawImage(srcC, sx0, sy0 + y + dy, width, BAND, 0, y, width, BAND);
    }
  }

  /**
   * Rotates the extended `mid` image by `angle` into a square rotated
   * workspace, centering the image so the visible region survives the
   * rotation. Used by the `diagonal`/`diamonds` textures.
   * @param {CanvasRenderingContext2D} dctx - Rotated workspace context.
   * @param {number} angle - Rotation angle in radians.
   * @returns {void}
   */
  function rotateIn(dctx, angle) {
    dctx.setTransform(1, 0, 0, 1, 0, 0);
    dctx.clearRect(0, 0, R, R);
    dctx.translate(R / 2, R / 2);
    dctx.rotate(angle);
    dctx.drawImage(mid, -EW / 2, -EH / 2);
    dctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /**
   * Rotates a square rotated workspace back onto the visible canvas at
   * `angle`, undoing the `rotateIn` transform.
   * @param {HTMLCanvasElement} srcC - Rotated workspace canvas.
   * @param {number} angle - Rotation angle in radians (inverse of `rotateIn`).
   * @returns {void}
   */
  function rotateOut(srcC, angle) {
    ctx.clearRect(0, 0, W, H);
    ctx.setTransform(1, 0, 0, 1, W / 2, H / 2);
    ctx.rotate(angle);
    ctx.drawImage(srcC, -R / 2, -R / 2);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /**
   * Upscales/blurs the low-res blob render into `mid`, then applies the
   * selected glass texture's displacement passes into the visible canvas,
   * finishing with the specular highlight pass. This is the main per-frame
   * compositing step, dispatching on `settings.texture`.
   * @returns {void}
   */
  function composite() {
    mctx.clearRect(0, 0, EW, EH);
    mctx.drawImage(small, 0, 0, EW, EH);

    const tex = settings.texture;

    if (tex === "none" || settings.glassStrength === 0) {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(mid, MARGIN, MARGIN, W, H, 0, 0, W, H);
      if (tex !== "none") applyHighlights();
      return;
    }

    if (tex === "fluted") {
      ctx.clearRect(0, 0, W, H);
      colShiftX(mid, ctx, W, H, MARGIN, MARGIN, waveSin);
    } else if (tex === "reeded") {
      ctx.clearRect(0, 0, W, H);
      rowShiftY(mid, ctx, W, H, MARGIN, MARGIN, waveSin);
    } else if (tex === "ripple") {
      // Pass 1: rows shifted horizontally by dx(y); pass 2: columns shifted vertically by dy(x)
      tctx.clearRect(0, 0, EW, EH);
      for (let y = 0; y < EH; y += BAND) {
        const dx = clampOff(rippleDX(y - MARGIN));
        tctx.drawImage(mid, dx, y, EW, BAND, 0, y, EW, BAND);
      }
      ctx.clearRect(0, 0, W, H);
      for (let x = 0; x < W; x += BAND) {
        const dy = clampOff(rippleDY(x));
        ctx.drawImage(temp, MARGIN + x, MARGIN + dy, BAND, H, x, 0, BAND, H);
      }
    } else if (tex === "cubes") {
      // Cross-fluted: vertical reeds then horizontal reeds → square lens cells
      tctx.clearRect(0, 0, EW, EH);
      for (let x = 0; x < EW; x += BAND) {
        const dx = clampOff(waveSin(x - MARGIN));
        tctx.drawImage(mid, x + dx, 0, BAND, EH, x, 0, BAND, EH);
      }
      ctx.clearRect(0, 0, W, H);
      rowShiftY(temp, ctx, W, H, MARGIN, MARGIN, waveSin);
    } else if (tex === "melt") {
      // Pass 1: columns drip vertically by chaotic amounts; pass 2: rows
      // smear sideways — the field looks like it's slowly liquefying
      tctx.clearRect(0, 0, EW, EH);
      for (let x = 0; x < EW; x += BAND) {
        const dy = clampOff(meltDY(x - MARGIN));
        tctx.drawImage(mid, x, dy, BAND, EH, x, 0, BAND, EH);
      }
      ctx.clearRect(0, 0, W, H);
      for (let y = 0; y < H; y += BAND) {
        const dx = clampOff(meltDX(y));
        ctx.drawImage(temp, MARGIN + dx, MARGIN + y, W, BAND, 0, y, W, BAND);
      }
    } else if (tex === "moire") {
      // Same two-pass shape as ripple, but with coarse+fine interference waves
      tctx.clearRect(0, 0, EW, EH);
      for (let y = 0; y < EH; y += BAND) {
        const dx = clampOff(moireDX(y - MARGIN));
        tctx.drawImage(mid, dx, y, EW, BAND, 0, y, EW, BAND);
      }
      ctx.clearRect(0, 0, W, H);
      for (let x = 0; x < W; x += BAND) {
        const dy = clampOff(moireDY(x));
        ctx.drawImage(temp, MARGIN + x, MARGIN + dy, BAND, H, x, 0, BAND, H);
      }
    } else if (tex === "prism") {
      // Triangle-wave reeds: displacement snaps direction at each facet edge
      ctx.clearRect(0, 0, W, H);
      colShiftX(mid, ctx, W, H, MARGIN, MARGIN, prismDX);
    } else if (tex === "cloud") {
      // Per-pixel displacement through the organic noise field, computed at
      // reduced resolution (the field is soft, so no crispness is lost)
      loctx.clearRect(0, 0, loW, loH);
      loctx.drawImage(mid, 0, 0, loW, loH);
      const srcD = loctx.getImageData(0, 0, loW, loH);
      const s = srcD.data,
        o = cloudOut.data;
      const Aq = settings.glassStrength * CLOUD_Q * 1.6; // clouds want a bit more push
      for (let y = 0; y < loH; y++) {
        const row = y * loW;
        for (let x = 0; x < loW; x++) {
          const idx = row + x;
          let sx = (x + cloudDX[idx] * Aq) | 0;
          let sy = (y + cloudDY[idx] * Aq) | 0;
          if (sx < 0) sx = 0;
          else if (sx >= loW) sx = loW - 1;
          if (sy < 0) sy = 0;
          else if (sy >= loH) sy = loH - 1;
          const si = (sy * loW + sx) * 4,
            oi = idx * 4;
          o[oi] = s[si];
          o[oi + 1] = s[si + 1];
          o[oi + 2] = s[si + 2];
          o[oi + 3] = s[si + 3];
        }
      }
      loctx.putImageData(cloudOut, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(
        lo,
        MARGIN * CLOUD_Q,
        MARGIN * CLOUD_Q,
        W * CLOUD_Q,
        H * CLOUD_Q,
        0,
        0,
        W,
        H,
      );
    } else if (tex === "diagonal") {
      // Rotate 45°, apply fluted reeds, rotate back → diagonal lines
      rotateIn(ractx, -THETA);
      rbctx.setTransform(1, 0, 0, 1, 0, 0);
      rbctx.clearRect(0, 0, R, R);
      colShiftX(rotA, rbctx, R, R, 0, 0, waveSin);
      rotateOut(rotB, THETA);
    } else if (tex === "diamonds") {
      // Rotate 45°, apply cross-fluted grid, rotate back → diamond cells
      rotateIn(ractx, -THETA);
      rbctx.setTransform(1, 0, 0, 1, 0, 0);
      rbctx.clearRect(0, 0, R, R);
      colShiftX(rotA, rbctx, R, R, 0, 0, waveSin);
      ractx.setTransform(1, 0, 0, 1, 0, 0);
      ractx.clearRect(0, 0, R, R);
      rowShiftY(rotB, ractx, R, R, 0, 0, waveSin);
      rotateOut(rotA, THETA);
    }

    applyHighlights();
  }

  // ---- Specular highlight mask ----

  /**
   * Builds the repeating specular-highlight alpha tile matching the current
   * `settings.texture` and `settings.glassScale`. The refracted image is
   * later re-drawn additively through this tile (see `applyHighlights`), so
   * bright zones appear only where gold is actually glowing behind the
   * glass. Must be rebuilt whenever `texture` or `glassScale` changes.
   * @returns {void}
   */
  function buildHighlightTile() {
    const L = Math.max(4, Math.round(settings.glassScale));
    const tex = settings.texture;
    const tc = tile.getContext("2d");

    /**
     * Specular response curve: raises a cosine/triangle sample to a power
     * so only near-peak values produce a visible highlight.
     * @param {number} c - Input sample, typically in [-1, 1].
     * @returns {number} Specular intensity, >= 0.
     */
    function spec(c) {
      return Math.pow(Math.max(0, c), 3);
    }

    if (tex === "fluted" || tex === "prism") {
      tile.width = L;
      tile.height = 1;
      const img = tc.createImageData(L, 1);
      for (let x = 0; x < L; x++) {
        let a;
        if (tex === "prism") {
          // Hard faceted glints: sharp peak on each zigzag facet
          const v = tri01(x, L);
          a =
            Math.pow(Math.max(0, v), 1.5) + 0.3 * Math.pow(Math.max(0, -v), 4);
        } else {
          const c = Math.cos((x * 2 * Math.PI) / L);
          a = spec(c) + 0.35 * Math.pow(Math.max(0, -c), 5);
        }
        const i = x * 4;
        img.data[i] = 255;
        img.data[i + 1] = 255;
        img.data[i + 2] = 255;
        img.data[i + 3] = Math.round(Math.min(1, a) * 255);
      }
      tc.putImageData(img, 0, 0);
    } else if (tex === "reeded") {
      tile.width = 1;
      tile.height = L;
      const img = tc.createImageData(1, L);
      for (let y = 0; y < L; y++) {
        const c = Math.cos((y * 2 * Math.PI) / L);
        const a = spec(c) + 0.35 * Math.pow(Math.max(0, -c), 5);
        const i = y * 4;
        img.data[i] = 255;
        img.data[i + 1] = 255;
        img.data[i + 2] = 255;
        img.data[i + 3] = Math.round(Math.min(1, a) * 255);
      }
      tc.putImageData(img, 0, 0);
    } else if (tex === "diagonal" || tex === "diamonds") {
      // Stripes at 45°: alpha varies with (x+y); diamonds add the (x−y) family
      const T = Math.max(6, Math.round(L * Math.SQRT2));
      tile.width = T;
      tile.height = T;
      const img = tc.createImageData(T, T);
      for (let y = 0; y < T; y++) {
        for (let x = 0; x < T; x++) {
          let a = spec(Math.cos(((x + y) * 2 * Math.PI) / T));
          if (tex === "diamonds")
            a += spec(Math.cos(((x - y) * 2 * Math.PI) / T));
          const i = (y * T + x) * 4;
          img.data[i] = 255;
          img.data[i + 1] = 255;
          img.data[i + 2] = 255;
          img.data[i + 3] = Math.round(Math.min(1, a) * 255);
        }
      }
      tc.putImageData(img, 0, 0);
    } else if (tex === "cubes") {
      // Grid of crossing highlight lines
      tile.width = L;
      tile.height = L;
      const img = tc.createImageData(L, L);
      for (let y = 0; y < L; y++) {
        for (let x = 0; x < L; x++) {
          const a =
            spec(Math.cos((x * 2 * Math.PI) / L)) +
            spec(Math.cos((y * 2 * Math.PI) / L));
          const i = (y * L + x) * 4;
          img.data[i] = 255;
          img.data[i + 1] = 255;
          img.data[i + 2] = 255;
          img.data[i + 3] = Math.round(Math.min(1, a) * 255);
        }
      }
      tc.putImageData(img, 0, 0);
    } else if (tex === "cloud") {
      // Soft cloudy patches from the same noise family as the displacement
      const T = Math.max(32, Math.min(256, Math.round(L * 3)));
      tile.width = T;
      tile.height = T;
      const img = tc.createImageData(T, T);
      for (let y = 0; y < T; y++) {
        for (let x = 0; x < T; x++) {
          const v = sampleWave(cloudWaves[0], x, y, T);
          const a = Math.pow(Math.max(0, v), 2) * 0.9;
          const i = (y * T + x) * 4;
          img.data[i] = 255;
          img.data[i + 1] = 255;
          img.data[i + 2] = 255;
          img.data[i + 3] = Math.round(a * 255);
        }
      }
      tc.putImageData(img, 0, 0);
    } else {
      // ripple / melt / moire fallback: soft 2D crests
      tile.width = L;
      tile.height = L;
      const img = tc.createImageData(L, L);
      for (let y = 0; y < L; y++) {
        for (let x = 0; x < L; x++) {
          const v =
            0.5 * Math.cos((x * 2 * Math.PI) / L + glass.p3) +
            0.5 * Math.cos((y * 2 * Math.PI) / L + glass.p1);
          const i = (y * L + x) * 4;
          img.data[i] = 255;
          img.data[i + 1] = 255;
          img.data[i + 2] = 255;
          img.data[i + 3] = Math.round(spec(v) * 255);
        }
      }
      tc.putImageData(img, 0, 0);
    }

    hlPattern = hctx.createPattern(tile, "repeat");
  }

  /**
   * Additively re-draws the current canvas frame through the specular
   * highlight tile (masked via `destination-in`), offset by the current
   * tilt position, so highlights track the simulated/device-orientation
   * light angle. No-ops when texture is `none`, `highlight` is 0, or the
   * tile hasn't been built yet.
   * @returns {void}
   */
  function applyHighlights() {
    if (settings.texture === "none" || settings.highlight <= 0 || !hlPattern)
      return;
    const tw = tile.width,
      th = tile.height;

    hctx.globalCompositeOperation = "source-over";
    hctx.clearRect(0, 0, W, H);
    hctx.drawImage(canvas, 0, 0);
    hctx.globalCompositeOperation = "destination-in";
    hctx.save();
    hctx.translate(tilt.x % tw, tilt.y % th);
    hctx.fillStyle = hlPattern;
    hctx.fillRect(-tw, -th, W + 2 * tw, H + 2 * th);
    hctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = Math.min(1, settings.highlight);
    ctx.drawImage(hl, 0, 0);
    if (settings.highlight > 1) {
      ctx.globalAlpha = settings.highlight - 1;
      ctx.drawImage(hl, 0, 0);
    }
    ctx.restore();
  }

  /**
   * Renders one complete frame at time `t`: draws the blobs, then composites
   * the glass texture and highlight passes on top.
   * @param {number} t - Current animation time (already scaled by speed).
   * @returns {void}
   */
  function renderFrame(t) {
    drawBlobs(t);
    composite();
  }

  let start = performance.now();
  /** Whether the rAF loop is currently allowed to schedule its next frame. */
  let running = false;
  /** Whether the hero section is currently intersecting the viewport. */
  let visible = true;

  /**
   * The main animation loop callback. Renders the current frame and, while
   * still `running` (visible and not reduced-motion), schedules the next
   * frame via `requestAnimationFrame`.
   * @param {number} now - The timestamp supplied by `requestAnimationFrame`.
   * @returns {void}
   */
  function loop(now) {
    const t = ((now - start) / 1000) * settings.speed;
    renderFrame(t);
    if (running && !reduceMotion) requestAnimationFrame(loop);
  }

  /**
   * Starts (or resumes) the animation loop. Re-anchors the clock's `start`
   * timestamp so elapsed time doesn't jump after being paused. No-ops under
   * reduced motion (that path only ever renders a single static frame) or
   * if already running.
   * @returns {void}
   */
  function startLoop() {
    if (reduceMotion || running || ageGateOpen()) return;
    running = true;
    start = performance.now();
    requestAnimationFrame(loop);
  }

  /**
   * Whether the age gate is up (sections/global-modal.liquid sets
   * `gc-age-gate-open` on <html> while the strict page-load gate is open and
   * fires `gc:age-gate-closed` on window when it closes). Behind the gate's
   * 85% black, blurred overlay the effect can't be seen, but each frame costs
   * ~10 ms of main thread on a desktop CPU (several times that on a phone),
   * 60 times a second, for as long as the visitor reads the gate. So the hero
   * shows one static frame until the gate closes. (perf, 2026-10-07)
   * @returns {boolean}
   */
  function ageGateOpen() {
    return document.documentElement.classList.contains("gc-age-gate-open");
  }

  /**
   * Stops the animation loop from scheduling further frames. The
   * currently-rendered frame remains on screen.
   * @returns {void}
   */
  function stopLoop() {
    running = false;
  }

  /**
   * Renders exactly one frame at a pseudo-random time offset. Used for the
   * reduced-motion path (a single static frame, never animated) and to
   * refresh the frame immediately after a tilt update while reduced motion
   * is active.
   * @returns {void}
   */
  function renderStatic() {
    renderFrame(rand(0, 100));
  }

  // ---- Tilt (device orientation) ----

  /**
   * Handles `deviceorientation` events: converts device beta/gamma into a
   * clamped tilt offset (relative to the first reading, so the starting
   * device angle is treated as "centered"). Under reduced motion, renders a
   * single refreshed static frame so the tilt-driven highlight still updates
   * without starting the animation loop.
   * @param {DeviceOrientationEvent} e - The device orientation event.
   * @returns {void}
   */
  function onOrientation(e) {
    if (e.beta == null || e.gamma == null) return;
    if (tilt.baseBeta == null) {
      tilt.baseBeta = e.beta;
      tilt.baseGamma = e.gamma;
    }
    const gx = (e.gamma - tilt.baseGamma) * 2.5;
    const gy = (e.beta - tilt.baseBeta) * 2.5;
    tilt.x = Math.max(-80, Math.min(80, gx));
    tilt.y = Math.max(-80, Math.min(80, gy));
    tilt.on = true;
    if (reduceMotion) renderStatic();
  }

  /**
   * Requests device-orientation permission (iOS 13+ gesture-gated API) if
   * needed, then subscribes to `deviceorientation` events. Safe to call even
   * where no orientation sensor exists (Tilt state simply never turns on;
   * the pointer-drag simulation below remains available as a fallback).
   * This runs unconditionally on init (no panel button to gate it) since
   * the production hero has no UI affordance to trigger it manually; the
   * permission prompt (where required) is a no-op until a user gesture
   * grants it, and browsers without the gated API subscribe immediately.
   * @returns {Promise<void>}
   */
  async function initTilt() {
    try {
      if (
        typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function"
      ) {
        // Cannot be requested without a user gesture on iOS; skip silently.
        // The drag-to-simulate fallback below still works regardless.
        return;
      }
      window.addEventListener("deviceorientation", onOrientation);
    } catch (err) {
      // No sensor / unsupported — drag-to-simulate remains available.
    }
  }

  // ---- Drag-to-simulate tilt (fallback input) ----
  /**
   * Wire pointer-drag on the hero section to the tilt offset, so previews
   * without a device-orientation sensor can still exercise the parallax/tilt
   * response. No-op if the hero section is absent. Suppressed once real
   * device-orientation input takes over (tilt.on).
   * @returns {void}
   */
  (function initDragTilt() {
    if (!heroSection) return;
    let dragging = false,
      lastX = 0,
      lastY = 0;
    heroSection.addEventListener("pointerdown", (e) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
    });
    window.addEventListener("pointermove", (e) => {
      if (!dragging || tilt.on) return;
      tilt.x += (e.clientX - lastX) * 0.6;
      tilt.y += (e.clientY - lastY) * 0.6;
      lastX = e.clientX;
      lastY = e.clientY;
      if (reduceMotion) renderStatic();
    });
    window.addEventListener("pointerup", () => {
      dragging = false;
    });
    window.addEventListener("pointercancel", () => {
      dragging = false;
    });
  })();

  /**
   * Reads and parses the injected `<script type="application/json"
   * id="hero-effect-config">` block, if present.
   * @returns {Object|null} The parsed config object, or `null` if the block
   *   is absent or fails to parse.
   */
  function readInjectedConfig() {
    const el = document.getElementById(CONFIG_SCRIPT_ID);
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  /**
   * Applies the injected config object onto the internal `settings` object,
   * mapping the JSON contract's keys (and legacy metaobject-key aliases) to
   * their internal `settings` names, normalizing `movement`/`texture`
   * labels to code keys, and validating/applying `base_color`. Returns
   * whether `settings.count` changed (so the caller knows to rebuild blobs).
   * @param {Object} config - The parsed injected config object.
   * @returns {boolean} True if `settings.count` changed as a result.
   */
  function applyConfig(config) {
    const prevCount = settings.count;

    if ("movement" in config) {
      settings.movement = normalizeKey(
        config.movement,
        MOVEMENT_MAP,
        MOVEMENT_KEYS,
        "lava",
      );
    }
    if ("texture" in config) {
      settings.texture = normalizeKey(
        config.texture,
        TEXTURE_MAP,
        TEXTURE_KEYS,
        "fluted",
      );
    }
    if (typeof config.opacity === "number") settings.opacity = config.opacity;
    if (typeof config.speed === "number") settings.speed = config.speed;
    if (typeof config.light_count === "number")
      settings.count = config.light_count;
    if (typeof config.light_size === "number")
      settings.sizeScale = config.light_size;
    if (typeof config.sharp_lights === "number")
      settings.edgeVariance = config.sharp_lights;

    // `breath` per the JSON contract; `breathing` accepted as a raw
    // metaobject-key alias in case Liquid passes it through unmapped.
    if (typeof config.breath === "number") settings.breath = config.breath;
    else if (typeof config.breathing === "number")
      settings.breath = config.breathing;

    if (typeof config.glow_pulse === "number")
      settings.glowPulse = config.glow_pulse;
    if (typeof config.parallax === "number")
      settings.parallax = config.parallax;

    // `glass_strength` per the JSON contract; `refraction_strength` accepted
    // as a raw metaobject-key alias.
    if (typeof config.glass_strength === "number")
      settings.glassStrength = config.glass_strength;
    else if (typeof config.refraction_strength === "number")
      settings.glassStrength = config.refraction_strength;

    // `glass_scale` per the JSON contract; `texture_scale` accepted as a raw
    // metaobject-key alias.
    if (typeof config.glass_scale === "number")
      settings.glassScale = config.glass_scale;
    else if (typeof config.texture_scale === "number")
      settings.glassScale = config.texture_scale;

    if (typeof config.highlight === "number")
      settings.highlight = config.highlight;

    const baseColor = isValidHex(config.base_color)
      ? config.base_color
      : "#ffc626";
    rebuildPalette(baseColor);

    return settings.count !== prevCount;
  }

  // ---- IntersectionObserver pause (offscreen) ----
  /**
   * Wires up an IntersectionObserver on the hero section so the animation
   * loop is stopped while offscreen and resumed when it re-enters the
   * viewport. No-ops (loop always runs once started) if the hero section or
   * `IntersectionObserver` itself is unavailable, or under reduced motion.
   * @returns {void}
   */
  function initVisibilityObserver() {
    if (
      reduceMotion ||
      !heroSection ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting;
        if (visible) startLoop();
        else stopLoop();
      }
    });
    observer.observe(heroSection);
  }

  // ---- Init ----
  window.addEventListener("resize", () => {
    resize();
  });
  resize();
  rebuildPalette("#ffc626"); // brand gold default, overridden by config below if present

  // buildBlobs() always runs on first init (regardless of whether count
  // changed) since blobs haven't been created yet; applyConfig's return
  // value only matters for a hypothetical live-reconfigure path, which this
  // production entry point doesn't expose (config is read once, on load).
  const injected = readInjectedConfig();
  if (injected) applyConfig(injected);

  buildBlobs();
  buildHighlightTile();
  if (settings.texture === "cloud") buildCloudField();

  initTilt();
  initVisibilityObserver();

  if (reduceMotion) {
    renderStatic();
  } else if (ageGateOpen()) {
    renderStatic();
    window.addEventListener(
      "gc:age-gate-closed",
      () => {
        if (visible) startLoop();
      },
      { once: true }
    );
  } else {
    startLoop();
  }
})();
